import { useState, useRef } from "react";
import { View, StyleSheet, Text, Platform, TouchableOpacity } from "react-native";
import { platformAlert } from "../../utils/platformAlert";
import { GoogleButton } from "../../components/auth/GoogleButton";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useColors, ThemeColors } from "../../hooks/useColors";
import { spacing } from "../../theme";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { Checkbox } from "../ui/Checkbox";
import { Modal } from "../ui/Modal";
import { Ionicons } from "@expo/vector-icons";
import {
  signInRequest,
  signUpRequest,
  initializeRequest,
} from "../../store/slices/authSlice";
import { RootState, AppDispatch } from "../../store";

import { validateEmail, validatePassword } from "../../helpers/validators";
import {
  formatPhoneNumberAsUserTypes,
  validatePhoneNumberWithError,
  formatPhoneNumberForAPI,
} from "../../utils/phoneValidation";

// Platform-specific imports for sign-in hooks
import { useGoogleSignIn } from "../../hooks/useGoogleSignIn";
import { useAppleSignIn } from "../../hooks/useAppleSignIn";

// Conditionally import Apple Button only for iOS native
let AppleButton: any = null;
if (Platform.OS === 'ios') {
  try {
    const appleAuth = require('@invertase/react-native-apple-authentication');
    AppleButton = appleAuth.AppleButton;
  } catch (e) {
    console.log('[AuthForm] Apple authentication not available');
  }
}

interface AuthFormProps {
  mode?: "signin" | "signup";
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
}


export function AuthForm({
  mode = "signin",
  onNavigateToTerms,
  onNavigateToPrivacy,
}: AuthFormProps) {
  const { t } = useTranslation();
  const colors = useColors();
  const styles = createStyles(colors);
  const dispatch = useDispatch<AppDispatch>();
  
  const { signIn: appleSignIn } = useAppleSignIn(() => {
    dispatch(initializeRequest());
  });
  const { signIn: googleSignIn, loading: isGoogleLoading } = useGoogleSignIn(
    () => {
      dispatch(initializeRequest());
    }
  );
  
  const authState = useSelector((state: RootState) => state.auth);
  const { loading, error } = authState;

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(true);
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false);
  const [authMethod, setAuthMethod] = useState<"email" | "phone">("email");
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [modalTermsChecked, setModalTermsChecked] = useState(false);
  const pendingAuthAction = useRef<"google" | "apple" | "email" | null>(null);

  const showAlert = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      window.alert(`${title}\n\n${message}`);
    } else {
      platformAlert(title, message);
    }
  };

  const handleSubmit = () => {
    // Email validation
    if (!validateEmail(email)) {
      return showAlert(t("auth.invalidEmail"), t("auth.enterValidEmail"));
    }

    // Password validation
    if (!validatePassword(password)) {
      return showAlert(t("auth.passwordTooShort"), t("auth.passwordLength"));
    }
    if (mode === "signup" && password !== confirmPassword) {
      return showAlert(
        t("auth.passwordMismatch"),
        t("auth.passwordsDoNotMatch")
      );
    }
    
    // Show terms modal only for signup if not agreed yet
    if (mode === "signup" && !hasAgreedToTerms) {
      pendingAuthAction.current = "email";
      setModalTermsChecked(false);
      setShowTermsModal(true);
      return;
    }

    executeEmailAuth();
  };

  const executeEmailAuth = () => {
    const authData = { email, password };

    if (mode === "signin") {
      dispatch(signInRequest(authData));
    } else {
      dispatch(signUpRequest(authData));
    }
  };

  const handlePhoneChange = (text: string) => {
    const formatted = formatPhoneNumberAsUserTypes(text);
    setPhone(formatted);
  };

  const handleAppleSignIn = async () => {
    if (!hasAgreedToTerms) {
      pendingAuthAction.current = "apple";
      setModalTermsChecked(false);
      setShowTermsModal(true);
      return;
    }
    const result = await appleSignIn();
    console.log("Apple Sign-In result:", result);
  };

  const handleGoogleSignIn = async () => {
    if (!hasAgreedToTerms) {
      pendingAuthAction.current = "google";
      setModalTermsChecked(false);
      setShowTermsModal(true);
      return;
    }
    const result = await googleSignIn();
    console.log("Google Sign-In result:", result);
  };

  const handleModalContinue = async () => {
    if (!modalTermsChecked) {
      return;
    }
    setHasAgreedToTerms(true);
    setShowTermsModal(false);
    
    if (pendingAuthAction.current === "google") {
      const result = await googleSignIn();
      console.log("Google Sign-In result:", result);
    } else if (pendingAuthAction.current === "apple") {
      const result = await appleSignIn();
      console.log("Apple Sign-In result:", result);
    } else if (pendingAuthAction.current === "email") {
      executeEmailAuth();
    }
    pendingAuthAction.current = null;
  };

  const handleModalClose = () => {
    setShowTermsModal(false);
    setModalTermsChecked(false);
    pendingAuthAction.current = null;
  };

  // Render Apple Sign-In button based on platform
  const renderAppleButton = () => {
    // On iOS native, use the native Apple button
    if (Platform.OS === 'ios' && AppleButton) {
      return (
        <AppleButton
          buttonStyle={AppleButton.Style.BLACK}
          buttonType={AppleButton.Type.SIGN_IN}
          style={styles.appleButton}
          onPress={handleAppleSignIn}
        />
      );
    }
    
    // Don't show Apple button on web or Android
    return null;
  };

  return (
    <View style={styles.container}>
      {/* Email Input */}
      <Input
        label={t("auth.email")}
        value={email}
        onChangeText={setEmail}
        autoCapitalize="none"
        keyboardType="email-address"
        autoCorrect={false}
        iconName={"mail-outline"}
      />

      <Input
        label={t("auth.password")}
        value={password}
        onChangeText={setPassword}
        autoCapitalize="none"
        isPassword
        setPaswordVisibility={setIsPasswordVisible}
        isPasswordVisible={isPasswordVisible}
        autoCorrect={false}
        iconName={"lock-closed-outline"}
      />
      {mode === "signup" && (
        <Input
          label={t("auth.confirmPassword")}
          value={confirmPassword}
          onChangeText={setConfirmPassword}
          autoCapitalize="none"
          isPassword
          setPaswordVisibility={setIsPasswordVisible}
          isPasswordVisible={isPasswordVisible}
          autoCorrect={false}
          iconName={"lock-closed-outline"}
        />
      )}
      <Button onPress={handleSubmit} loading={loading} style={{ marginTop: spacing.md }}>
        {mode === "signin" ? t("auth.signIn") : t("auth.signUp")}
      </Button>

      <Text style={styles.orText}>⎯⎯⎯⎯⎯ or ⎯⎯⎯⎯⎯</Text>
      
      {renderAppleButton()}
      
      <GoogleButton
        onPress={handleGoogleSignIn}
        loading={isGoogleLoading}
      />

      {/* Terms Agreement Modal */}
      <Modal visible={showTermsModal} onClose={handleModalClose}>
        <View style={styles.modalContent}>
          <View style={styles.modalIconContainer}>
            <Ionicons name="shield-checkmark" size={32} color={colors.primary} />
          </View>
          <Text style={styles.modalTitle}>Agreement Required</Text>
          <Text style={styles.modalDescription}>
            To continue with your sign-in, please agree to our Terms & Conditions and Privacy Policy.
          </Text>
          
          <TouchableOpacity 
            style={styles.modalAgreementContainer}
            onPress={() => setModalTermsChecked(!modalTermsChecked)}
            activeOpacity={0.7}
          >
            <Checkbox
              checked={modalTermsChecked}
              onPress={() => setModalTermsChecked(!modalTermsChecked)}
            />
            <View style={styles.agreementTextContainer}>
              <Text style={styles.agreementText}>
                I have read and agree to the{" "}
                <Text style={styles.linkText} onPress={onNavigateToTerms}>
                  Terms
                </Text>{" "}
                &{" "}
                <Text style={styles.linkText} onPress={onNavigateToPrivacy}>
                  Privacy Policy
                </Text>
              </Text>
            </View>
          </TouchableOpacity>

          <View style={styles.modalButtonsContainer}>
            <Button 
              onPress={handleModalContinue} 
              disabled={!modalTermsChecked}
              style={!modalTermsChecked ? { ...styles.modalButton, ...styles.disabledButton } : styles.modalButton}
            >
              Continue
            </Button>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      gap: 4,
    },
    appleButton: {
      height: 45,
    },
    orText: {
      textAlign: "center",
      color: colors.textLight,
      marginVertical: spacing.sm,
    },
    agreementContainer: {
      flexDirection: "row",
      alignItems: "flex-start",
      marginTop: spacing.md,
      marginBottom: spacing.sm,
    },
    agreementTextContainer: {
      flex: 1,
      marginLeft: spacing.sm + 4,
    },
    agreementText: {
      fontSize: 14,
      color: colors.textLight,
      lineHeight: 20,
    },
    linkText: {
      color: colors.primary,
      textDecorationLine: "underline",
    },
    authMethodContainer: {
      marginBottom: spacing.md,
    },
    authMethodButtons: {
      flexDirection: "row",
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 4,
    },
    authMethodButton: {
      flex: 1,
      textAlign: "center",
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
      fontSize: 14,
      fontWeight: "600",
      color: colors.textLight,
      borderRadius: 6,
    },
    authMethodButtonActive: {
      backgroundColor: colors.primary,
      color: colors.background,
    },
    // Modal styles
    modalContent: {
      padding: spacing.xl,
      paddingTop: spacing.md,
    },
    modalIconContainer: {
      width: 64,
      height: 64,
      borderRadius: 32,
      backgroundColor: '#E6F5EF',
      alignItems: "center",
      justifyContent: "center",
      alignSelf: "center",
      marginBottom: spacing.md,
    },
    modalTitle: {
      fontSize: 24,
      fontWeight: "800",
      color: colors.text,
      textAlign: "center",
      marginBottom: spacing.xs,
      letterSpacing: -0.5,
    },
    modalDescription: {
      fontSize: 16,
      color: colors.textSecondary,
      textAlign: "center",
      marginBottom: spacing.xl,
      lineHeight: 22,
    },
    modalAgreementContainer: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: spacing.xl,
      backgroundColor: colors.backgroundSecondary,
      padding: spacing.lg,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: colors.borderLight,
    },
    modalButtonsContainer: {
      marginTop: spacing.sm,
    },
    modalButton: {
      height: 56,
      borderRadius: 14,
    },
    disabledButton: {
      opacity: 0.6,
      backgroundColor: colors.textLight,
    },
  });
